<script>
  import * as Pancake from "@sveltejs/pancake";
  import * as d3 from "d3-hierarchy";
  import { tick } from "svelte";
  import { tweened } from "svelte/motion";
  import * as eases from "svelte/easing";
  import Treemap from "./Treemap.svelte";
  import LoadingIndicator from "lib/components/LoadingIndicator.svelte";
  import { formatPercentage } from "lib/util/formatfunctions.js";
  import { Loading } from "carbon-components-svelte";

  export let treeData;
  export let level;

  export let formatfunction = (d) => d;
  export let loading = false;
  export let currentApp = ["Applications"];
  export let responsiveLabels = false;
  export let pinOthersLast = false;
  export let showAppTypes = false;
  export let appTypesByName = {};
  export let appInfoByName = {};
  export let windowInfoByName = {};
  export let appTypeTotals = {};
  export let nodeColors = {};

  export async function downloadSVG() {
    if (!treemapGraph) return;

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");

    const targetClasses = [
      ".treemap_wrapper",
      ".breadcrumbs",
      ".breadcrumbs_wrapper",
      ".chart",
      ".pancake-chart",
      "pancake-treemap",
      ".pancake-box",
      ".node",
      ".contents",
      "strong",
      "span",
      "div",
      ".bold",
    ];

    const rect = treemapGraph.getBoundingClientRect();
    svg.setAttribute("width", rect.width);
    svg.setAttribute("height", rect.height);
    svg.setAttribute("viewBox", `0 0 ${rect.width} ${rect.height}`);

    const foreignObject = document.createElementNS(svgNS, "foreignObject");
    const styleSheets = Array.from(document.styleSheets);
    let cssText = ` 
     @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      * {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        letter-spacing: 0.16px;
        font-size: 0.875rem;
        font-weight:400;
        line-height: 1.28572;
        line-height: 1;
        color: inherit;
      }
        .breadcrumbs{
        background-color: white !important;
        }
    `;

    styleSheets.forEach((sheet) => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules);
        rules.forEach((rule) => {
          if (rule.type === CSSRule.STYLE_RULE) {
            const matchesTarget = targetClasses.some(
              (target) =>
                rule.selectorText &&
                (rule.selectorText === target ||
                  rule.selectorText.startsWith(`${target}.svelte`))
            );
            if (matchesTarget) cssText += rule.cssText + "\n";
          }
        });
      } catch (e) {
        //ignore CORS errors
      }
    });
    cssText = cssText.replace(/cursor:\s*pointer\s*;/g, "");
    cssText = cssText.replace(/\.contents[^}]*:hover[^}]*}/g, "");
    if (cssText) {
      const styleElement = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "style"
      );
      styleElement.textContent = cssText;
      svg.insertBefore(styleElement, svg.firstChild);
    }

    foreignObject.setAttribute("width", "100%");
    foreignObject.setAttribute("height", "100%");

    const htmlClone = treemapGraph.cloneNode(true);

    foreignObject.appendChild(htmlClone);
    svg.appendChild(foreignObject);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentApp}_AppDrilldown.svg`;
    a.click();

    URL.revokeObjectURL(url);
  }

  const treemap = d3.treemap();
  function isOthersNode(node) {
    const name = `${node.data?.name || ""}`.toLowerCase();
    return name === "others" || name === "weitere anwendungen";
  }

  function categoryColor(node) {
    const category = node.ancestors?.().find((item) => item.depth === 1);
    const key = `${category?.data?.name || ""}`.trim().toLowerCase();
    return nodeColors[key] || "";
  }

  function sortNodes(a, b) {
    if (pinOthersLast && a.depth === b.depth) {
      const aIsOthers = isOthersNode(a);
      const bIsOthers = isOthersNode(b);
      if (aIsOthers !== bIsOthers) return aIsOthers ? 1 : -1;
    }

    return b.value - a.value;
  }

  const hierarchy = d3.hierarchy(treeData).sum((d) => d.value).sort(sortNodes);
  const root = treemap(hierarchy);

  let internallevel = level;
  let previousLevel1;
  let previousLevel2;
  let selected = root;

  let treemapGraph;
  let tooltipAppInfo = null;
  let tooltipTitle = "";
  let tooltipShare = "";
  let tooltipX = 0;
  let tooltipY = 0;
  let drilldownLoading = false;
  const tooltipWidth = 256;
  const tooltipHeight = 240;
  const tooltipOffset = 14;

  $: {
    if (currentApp[0]) {
      externalUpdate();
    }
  }

  $: {
    externalLevelChange(level);
  }

  $: {
    level = internallevel;
  }

  $: $extents = {
    x1: selected.x0,
    x2: selected.x1,
    y1: selected.y1,
    y2: selected.y0,
  };

  function externalLevelChange(level) {
    if (level < internallevel) {
      while (internallevel > level) {
        levelUp();
      }
    }
    if (level > internallevel) {
      if (level == 1) {
        select(previousLevel1);
      }
      if (level == 2) {
        if (internallevel == 0) select(previousLevel1);
        select(previousLevel2);
      }
    }
  }

  function externalUpdate() {
    if (currentApp[0] === "Applications") {
      select(root);
      externalLevelChange(0);
    } else {
      if (currentApp[0] !== selected.data.name && root.children) {
        let nextApp = currentApp[0];
        if (selected.children.find((item) => item.data.name === nextApp)) {
          select(selected.children.find((item) => item.data.name === nextApp));
        } else if (
          selected.parent &&
          selected.parent.find((item) => item.data.name === nextApp) &&
          level === 2
        ) {
          externalLevelChange(1);
          select(selected.parent.find((item) => item.data.name === nextApp));
        } else if (root.children.find((item) => item.data.name === nextApp)) {
          externalLevelChange(0);
          select(root.children.find((item) => item.data.name === nextApp));
        } else {
          const firstLevel = root.children.find(
            (item) => item.data.name === currentApp[1]
          );
          if (firstLevel) {
            const [prefix, ...rest] = currentApp[0].split(" - ");
            if (prefix && rest.length > 0) {
              currentApp[0] = rest.join(" - ");
            }
            const secondLevel = firstLevel.children?.find(
              (item) => item.data.name === currentApp[0]
            );
            if (secondLevel) {
              select(secondLevel);
            } else {
              select(root);
              externalLevelChange(0);
            }
          } else if (currentApp[1] === selected.parent?.data.name) {
            const [prefix, ...rest] = currentApp[0].split(" - ");
            if (prefix && rest.length > 0) {
              currentApp[0] = rest.join(" - ");
            }
            const appPath = [currentApp[0], selected.data.name, currentApp[1]];
            const first = root.children.find(
              (item) => item.data.name === selected.data.name
            );
            const second = first?.children?.find(
              (item) => item.data.name === currentApp[0]
            );
            select(second);
            currentApp = appPath;
            level === 2;
          } else {
            select(root);
            externalLevelChange(0);
          }
          // select((root.children.find((item) => item.data.name === currentApp[1]).find((item) => item.data.name === currentApp[0])));
        }
      }
    }
  }

  async function showDrilldownLoading() {
    drilldownLoading = true;
    hideTooltip();
    await tick();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    drilldownLoading = false;
  }

  function select(node) {
    if (node && node.children) {
      const isDrilldown = node.depth > selected.depth;
      if (isDrilldown) showDrilldownLoading();
      if (node.height === 2) {
        if (node != previousLevel1) previousLevel2 = null;
        previousLevel1 = node;
      }
      if (node.height === 1) {
        previousLevel2 = node;
      }
      selected = node;
      currentApp = [selected.data.name];
      internallevel = selected.depth;
      let parent = selected.parent;
      while (parent) {
        currentApp.push(parent.data.name);
        parent = parent.parent;
      }
    }
  }

  function levelUp() {
    if (selected.height === 0) return;
    if (selected.parent) {
      selected = selected.parent;
      currentApp[0] = selected.data.name;
      internallevel = selected.depth;
    }
  }

  const breadcrumbs = (node) => {
    const crumbs = [];
    while (node) {
      crumbs.unshift(node.data.name);
      node = node.parent;
    }

    return crumbs.join("/");
  };

  const extents = tweened(undefined, {
    easing: eases.cubicOut,
    duration: 600,
  });

  const is_visible = (a, b) => {
    while (b) {
      if (a.parent === b) return true;
      b = b.parent;
    }

    return false;
  };

  function appTypeKey(node, typeMapping = appTypesByName) {
    if (node.depth !== 1) return "";
    const type = typeMapping[`${node.data.name}`.trim().toLowerCase()];
    if (!type) return "";
    return type === "supportive" ? "support" : type;
  }

  function appTypeClass(node, typeMapping) {
    const type = appTypeKey(node, typeMapping);
    return type ? `app_type_${type}` : "";
  }

  function getDisplayedAppTypeLegend(typeMapping) {
    const totalsByType = {};
    const appNodes = root.children || [];

    appNodes.forEach((node) => {
      if (isOthersNode(node)) return;
      const type = appTypeKey(node, typeMapping);
      const key = type || "untyped";
      totalsByType[key] =
        (totalsByType[key] || 0) +
        Math.round((node.value / node.parent.value) * 100);
    });

    return totalsByType;
  }

  function nodeAppInfo(node) {
    if (node.depth === 2) {
      const appName = `${node.parent?.data?.name || ""}`.trim().toLowerCase();
      const windowTitle = `${node.data.name}`.trim().toLowerCase();
      return windowInfoByName[`${appName}\x1f${windowTitle}`];
    }
    return appInfoByName[`${node.data.name}`.trim().toLowerCase()];
  }

  function formatDuration(value) {
    if (value === null || value === undefined) return "-";
    const totalMinutes = Math.floor(Math.max(0, Number(value) || 0) / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }

  function formatCompactNumber(value) {
    const number = Number(value) || 0;
    if (Math.abs(number) >= 1_000_000)
      return `${(number / 1_000_000).toFixed(2)} M`;
    if (Math.abs(number) >= 1_000)
      return `${(number / 1_000).toFixed(1)} k`;
    return Math.round(number);
  }

  function tooltipItems(appInfo) {
    return [
      {
        key: "apps",
        label: "Share",
        value: tooltipShare || "-",
      },
      {
        icon: "time_dark",
        label: "Total Time",
        value: formatDuration(appInfo?.time),
      },
      {
        icon: "mouseclicks_dark",
        label: "Mouseclicks",
        value: formatCompactNumber(appInfo?.mouseclicks),
      },
      {
        icon: "text_fields",
        label: "Text Time",
        value: formatDuration(appInfo?.text_time),
      },
      {
        icon: "copy_dark",
        label: "Copy Time",
        value: formatDuration(appInfo?.copy_time),
      },
    ];
  }

  function showTooltip(event, node) {
    const appInfo = nodeAppInfo(node);
    if (!appInfo) return;
    const opensLeft =
      event.clientX + tooltipOffset + tooltipWidth > window.innerWidth;
    const opensUp =
      event.clientY + tooltipOffset + tooltipHeight > window.innerHeight;
    tooltipAppInfo = appInfo;
    tooltipTitle = node.data.name;
    tooltipShare = formatPercentage(node.value / node.parent.value);
    tooltipX = opensLeft
      ? event.clientX - tooltipWidth - tooltipOffset
      : event.clientX + tooltipOffset;
    tooltipY = opensUp
      ? event.clientY - tooltipHeight - tooltipOffset
      : event.clientY + tooltipOffset;
  }

  function hideTooltip() {
    tooltipAppInfo = null;
  }

  function portal(node) {
    document.body.appendChild(node);

    return {
      destroy() {
        node.remove();
      },
    };
  }

  $: appTypeTotals = getDisplayedAppTypeLegend(appTypesByName);

  $$restProps;
</script>

<div class="treemap_wrapper">
  <button class="breadcrumbs" disabled={!selected.parent} on:click={levelUp}>
    <div class="breadcrumbs_wrapper">
      <div class="text bold">
        Treemap Navigation
        {#if selected.parent}
          <span class="breadcrumbs_hint">← Click here to go back one level</span>
        {/if}
      </div>
      <div class="breadcrums">{breadcrumbs(selected)}</div>
    </div>
  </button>

  <div
    bind:this={treemapGraph}
    class="chart"
    class:responsive_labels={responsiveLabels}
    class:app_types_hidden={!showAppTypes}
  >
    {#if drilldownLoading}
      <div class="drilldown_loading">
        <LoadingIndicator label="Loading tiles" />
      </div>
    {/if}
    <Pancake.Chart
      x1={$extents.x1}
      x2={$extents.x2}
      y1={$extents.y1}
      y2={$extents.y2}
    >
      {#if loading}
        <div class="loading_wrapper">
          <Loading withOverlay={false} />
        </div>
      {:else if !drilldownLoading}
        <Treemap {root} let:node>
          {#if is_visible(node, selected)}
            <div
              class="node"
              class:leaf={!node.children}
              class:others={isOthersNode(node)}
              class:category_color={Boolean(categoryColor(node))}
              style={`--category-tile-color: ${categoryColor(node)}`}
              class:app_type_main={appTypeClass(node, appTypesByName) === "app_type_main"}
              class:app_type_unwanted={appTypeClass(node, appTypesByName) ===
                "app_type_unwanted"}
              class:app_type_support={appTypeClass(node, appTypesByName) ===
                "app_type_support"}
              class:app_type_communication={appTypeClass(node, appTypesByName) ===
                "app_type_communication"}
              on:mousemove={(event) => showTooltip(event, node)}
              on:mouseleave={hideTooltip}
              on:mousedown={() => select(node)}
            >
              <div class="contents">
                <strong>{node.data.name}</strong>
                <span>{formatPercentage(node.value / node.parent.value)}</span>
                <span>{formatfunction(node.value)}</span>
              </div>
            </div>
          {/if}
        </Treemap>
      {/if}
    </Pancake.Chart>
  </div>
  {#if tooltipAppInfo}
    <div
      class="app_tooltip"
      use:portal
      style={`left: ${tooltipX}px; top: ${tooltipY}px;`}
    >
      <div class="app_tooltip_title">{tooltipTitle}</div>
      {#each tooltipItems(tooltipAppInfo) as item}
          <div class="app_tooltip_row">
          <img
            src={`/img/assistant/icons/${item.icon || "apps_dark"}.svg`}
            alt=""
          />
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .treemap_wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    font-size: 1rem;
    padding-bottom: 0.5rem;
    box-sizing: border-box;
  }

  .breadcrumbs_wrapper {
    display: flex;
    flex-direction: column;
    font-size: 1rem;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
  }

  .breadcrumbs_hint {
    color: var(--pax-grey);
    font-size: 0.75rem;
    font-weight: 400;
  }

  .breadcrumbs_wrapper > .text {
    align-items: center;
    display: flex;
    gap: 0.35rem;
    white-space: nowrap;
  }

  .breadcrumbs {
    width: 100%;
    padding: 0.3rem 0.4rem;
    background-color: transparent;
    text-align: left;
    border: none;
    cursor: pointer;
    outline: none;
    position: relative;
    z-index: 31;
    flex-shrink: 0;
  }

  .breadcrumbs:disabled {
    cursor: default;
  }

  .chart {
    width: 100%;
    height: 100%;
    padding: 0;
    overflow: hidden;
    position: relative;
    z-index: 30;
  }

  .drilldown_loading {
    position: absolute;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.78);
  }

  .node {
    width: 100%;
    height: 100%;
    background-color: white;
    overflow: visible;
    pointer-events: all;
    container-type: size;
    position: relative;
  }

  .node:hover {
    z-index: 10;
  }

  .node:not(.leaf) {
    cursor: pointer;
  }

  .contents {
    width: 100%;
    height: 100%;
    padding: 0.3rem 0.4rem;
    border: 0.25rem solid white;
    background-color: var(--pax-smoke, #f2f2f2);
    color: var(--pax-black);
    border-radius: 0.5rem;
    box-sizing: border-box;
    overflow: overlay;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .contents::-webkit-scrollbar {
    display: none;
  }

  .node:not(.leaf) .contents {
    background-color: #76bbea;
    transition: 300ms ease-in-out;
    color: white;
  }

  .node:not(.leaf) .contents:hover {
    opacity: 70%;
  }

  :global(.responsive_labels) .contents {
    width: calc(100% - 0.6rem);
    height: calc(100% - 0.6rem);
    margin: 0.3rem;
    background-color: #f2f2f2;
    color: var(--pax-black);
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
  }

  :global(.responsive_labels) .node:not(.leaf) .contents {
    background-color: #7c7c82;
    color: var(--pax-white);
  }

  :global(.responsive_labels) .contents:hover {
    background-color: #e8e8e8;
    opacity: 100%;
  }

  :global(.responsive_labels) .node:not(.leaf) .contents:hover {
    background-color: #88888e;
  }

  :global(.responsive_labels) .node.others .contents,
  :global(.responsive_labels) .node.others .contents:hover {
    background-color: var(--pax-white);
    color: var(--pax-black);
  }

  :global(.responsive_labels.app_types_hidden) .node .contents,
  :global(.responsive_labels.app_types_hidden) .node:not(.leaf) .contents,
  :global(.responsive_labels.app_types_hidden) .node.others .contents {
    background-color: #f2f2f2;
    color: var(--pax-black);
  }

  :global(.responsive_labels.app_types_hidden) .node .contents:hover,
  :global(.responsive_labels.app_types_hidden) .node:not(.leaf) .contents:hover,
  :global(.responsive_labels.app_types_hidden) .node.others .contents:hover {
    background-color: #e8e8e8;
    color: var(--pax-black);
  }

  :global(.responsive_labels) .node.app_type_main .contents {
    background-color: #b7e3bd;
    color: var(--pax-black);
  }

  :global(.responsive_labels) .node.app_type_main .contents:hover {
    background-color: #c9edce;
  }

  :global(.responsive_labels) .node.app_type_unwanted .contents {
    background-color: #e6a7ad;
    color: var(--pax-black);
  }

  :global(.responsive_labels) .node.app_type_unwanted .contents:hover {
    background-color: #efbcc1;
  }

  :global(.responsive_labels) .node.app_type_support .contents {
    background-color: #e5e8a8;
    color: var(--pax-black);
  }

  :global(.responsive_labels) .node.app_type_support .contents:hover {
    background-color: #eef0bd;
  }

  :global(.responsive_labels) .node.app_type_communication .contents {
    background-color: #abcfe8;
    color: var(--pax-black);
  }

  :global(.responsive_labels) .node.app_type_communication .contents:hover {
    background-color: #bfdcf0;
  }

  :global(.responsive_labels) .node.category_color .contents,
  :global(.responsive_labels) .node.category_color .contents:hover {
    background-color: var(--category-tile-color) !important;
    color: var(--pax-black) !important;
  }

  .app_tooltip {
    position: fixed;
    z-index: 2147483647;
    width: 16rem;
    padding: 0.95rem 1rem;
    border-radius: 0.5rem;
    background-color: var(--pax-black);
    color: var(--pax-white);
    box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.18);
    pointer-events: none;
  }

  .app_tooltip_title {
    margin-bottom: 0.75rem;
    font-size: 0.85rem;
    font-weight: 700;
    line-height: 1.15;
    color: var(--pax-white);
  }

  .app_tooltip_row {
    display: grid;
    grid-template-columns: 1rem minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.65rem;
    min-height: 1.9rem;
    color: var(--pax-white);
  }

  .app_tooltip_row img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }

  .app_tooltip_row span,
  .app_tooltip_row strong {
    padding: 0;
    color: var(--pax-white);
    font-size: 0.78rem;
    line-height: 1.1;
  }

  .app_tooltip_row strong {
    font-weight: 700;
  }

  strong,
  span {
    display: block;
    /* font-size: 12px; */
    white-space: normal;
    line-height: 1;
    overflow-wrap: break-word;
    padding-left: 0.5rem;
  }

  strong {
    padding-top: 0.5rem;
  }

  :global(.responsive_labels) strong {
    font-size: clamp(0.72rem, min(7cqw, 13cqh), 1.35rem);
    line-height: 1.05;
  }

  :global(.responsive_labels) span {
    font-size: clamp(0.68rem, min(4.8cqw, 8cqh), 1rem);
    line-height: 1.05;
  }

  :global(.responsive_labels) strong + span {
    font-size: clamp(0.86rem, min(6.3cqw, 10cqh), 1.28rem);
    font-weight: 700;
  }

  span + span {
    font-size: 0.6rem;
  }

  :global(.responsive_labels) span + span {
    font-size: clamp(0.72rem, min(5cqw, 8.5cqh), 1.05rem);
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    height: 100%;
  }
</style>
